// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { InstrumentConfiguration } from '@digitalasset/utility-operator-api-ts-client';
import useRegistryInstrumentConfigurationFactory from '@hooks/queries/registry/configuration/useRegistryInstrumentConfigurationFactory';
import useComplianceService from '@hooks/queries/registry/onboarding/useComplianceService';
import { useGetAllInstrumentConfigurations } from '@hooks/queries/useGetAllInstrumentConfigurations';
import { UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import { InstrumentConfiguration as DamlInstrumentConfiguration } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Configuration/Instrument';
import { ContractId } from '@daml/types';
import { SyncButton } from '../../../components/Button/SyncButton';
import CopyableText from '../../../components/CopyableText';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { QueryTable } from '../../../components/Table/QueryTable';
import { useParties } from '../../../contexts/PartiesContext';
import useAddComplianceLockInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useAddComplianceLockInstrumentConfiguration';
import useAddSecurityLockInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useAddSecurityLockInstrumentConfiguration';
import useArchiveInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveInstrumentConfiguration';
import useDisableFractionalSupportInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useDisableFractionalSupportInstrumentConfiguration';
import useDisableTransfersInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useDisableTransfersInstrumentConfiguration';
import useEnableFractionalSupportInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useEnableFractionalSupportInstrumentConfiguration';
import useEnableTransfersInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useEnableTransfersInstrumentConfiguration';
import usePauseInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/usePauseInstrumentConfiguration';
import useRemoveComplianceLockInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useRemoveComplianceLockInstrumentConfiguration';
import useRemoveSecurityLockInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useRemoveSecurityLockInstrumentConfiguration';
import useUnpauseInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useUnpauseInstrumentConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import { useCsvExport } from '../../../hooks/other/useCsvExport';
import useInstrumentSecurityControlService from '../../../hooks/queries/registry/onboarding/useInstrumentSecurityControlService';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import { TransferDisablementType } from '../../../utils/LedgerApiClientV2';
import { capitalizeWord } from '../../../utils/capitalizeWord';
import { formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import {
  SUC_ARC_INST_CONF,
  ERR_ARC_INST_CONF,
  SUC_PAUSE_INST_CONF,
  ERR_PAUSE_INST_CONF,
  SUC_UNPAUSE_INST_CONF,
  ERR_UNPAUSE_INST_CONF,
  SUC_ENABLE_FRACTIONAL_SUPPORT,
  ERR_ENABLE_FRACTIONAL_SUPPORT,
  SUC_DISABLE_FRACTIONAL_SUPPORT,
  ERR_DISABLE_FRACTIONAL_SUPPORT,
  SUC_DISABLE_TRANSFERS_INST_CONF,
  ERR_DISABLE_TRANSFERS_INST_CONF,
  SUC_ENABLE_TRANSFERS_INST_CONF,
  ERR_ENABLE_TRANSFERS_INST_CONF,
} from '../../../utils/strings';
import { prettifyDisablementType } from '../../../utils/transferDisablements';
import CreateDtccTokenInstrumentConfigurationDialog from './CreateDtccTokenInstrumentConfigurationDialog';
import { ModifyDtccTokenInstrumentConfigurationDialog } from './ModifyDtccTokenInstrumentConfigurationDialog';

type Row = InstrumentConfiguration;

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const RowActionsMenu = ({ actions, row }: { actions: Action[]; row: Row }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (actions.length === 0) return null;
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {actions.map(({ name, action }) => (
          <MenuItem
            key={name}
            onClick={() => {
              setAnchor(null);
              void action(row);
            }}
          >
            {name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const hasNoProperties = (propObj: Record<string, string> | undefined | null): boolean =>
  !propObj || Object.keys(propObj).length === 0;

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => (
    <RowActionsMenu actions={createActions(params.row)} row={params.row} />
  );

  return [
    {
      field: 'registrar',
      headerName: 'Registrar',
      valueGetter: (params) => params.row.payload.registrar,
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'instrumentId',
      headerName: 'InstrumentId',
      valueGetter: (params) => `${params.row.payload.defaultIdentifier.id}`,
      renderCell: (params) => <CopyableText value={`${params.row.payload.defaultIdentifier.id}`} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'tokenName',
      headerName: 'Token Name',
      valueGetter: (params) => `${params.row.payload.tokenName || ''}`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'tokenSym',
      headerName: 'Token Symbol',
      valueGetter: (params) => `${params.row.payload.tokenSymbol || ''}`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'cusip',
      headerName: 'CUSIP',
      valueGetter: (params) => `${params.row.payload.cusip || ''}`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'issueType',
      headerName: 'Issue Type',
      valueGetter: (params) => `${capitalizeWord(params.row.payload.issueType || '')}`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'issueSubtype',
      headerName: 'Issue Subtype',
      valueGetter: (params) => `${capitalizeWord(params.row.payload.issueSubtype || '')}`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'pauseState',
      headerName: 'Pause State',
      valueGetter: (params) => `${params.row.payload.pauseState}`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'transferDisablements',
      headerName: 'Transfer State',
      valueGetter: (params) =>
        params.row.payload.transferDisablements.length === 0 ? 'Enabled' : 'Disabled',
      renderCell: (params) => {
        const types = params.row.payload.transferDisablements;
        if (types.length === 0) {
          return <span>Enabled</span>;
        }
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            Disabled
            <Tooltip title={types.map(prettifyDisablementType).join(', ')} arrow>
              <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary' }} />
            </Tooltip>
          </span>
        );
      },
      flex: 150,
      filterable: false,
    },
    {
      field: 'fractionalSupport',
      headerName: 'Fractional',
      valueGetter: (params) => `${params.row.payload.fractionalSupport}`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'properties',
      headerName: 'Properties',
      valueGetter: (params) => {
        const values = params.row.payload.meta;
        if (hasNoProperties(values)) return '';
        return Object.entries(values)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
      },
      renderCell: (params) => {
        const values = params.row.payload.meta;
        if (hasNoProperties(values)) return <span>-</span>;
        const entries = Object.entries(values);
        const display = entries.map(([k, v]) => `${k}: ${v}`).join(', ');
        return (
          <Tooltip title={display} arrow>
            <span>
              {entries.length} {entries.length === 1 ? 'property' : 'properties'}
            </span>
          </Tooltip>
        );
      },
      flex: 150,
      filterable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      type: 'dateTime',
      valueGetter: (params) => parseTimestampToDate(params.row.payload.createdAt as Date),
      renderCell: (params) => formatTimestamp(params.row.payload.createdAt as Date),
      flex: 150,
      minWidth: 180,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      type: 'dateTime',
      // Every state change archives the instrument configuration and creates a replacement, so the
      // active contract's creation time is when the instrument was last changed.
      valueGetter: (params) => parseTimestampToDate(params.row.contract.createdAt as Date),
      renderCell: (params) => formatTimestamp(params.row.contract.createdAt as Date),
      flex: 150,
      minWidth: 180,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      width: 70,
      filterable: false,
    },
  ];
};

const Toolbar = ({
  handleExport,
  isExportDisabled,
  isCreateDisabled,
  isLoading,
  onCreateClick,
}: {
  handleExport: () => void;
  isExportDisabled: boolean;
  isCreateDisabled: boolean;
  isLoading: boolean;
  onCreateClick: () => void;
}) => {
  return (
    <GridToolbarContainer>
      {isLoading ? null : (
        <>
          <SyncButton onClick={onCreateClick} disabled={isCreateDisabled}>
            Create DTCC Token
          </SyncButton>
          <SyncButton onClick={handleExport} disabled={isExportDisabled}>
            Export CSV
          </SyncButton>
        </>
      )}
    </GridToolbarContainer>
  );
};

export const InstrumentConfigurations = () => {
  const [
    createDtccTokenInstrumentConfigurationIsOpen,
    setCreateDtccTokenInstrumentConfigurationIsOpen,
  ] = useState(false);
  const [modifyInstrumentConfiguration, setModifyInstrumentConfiguration] = useState<
    InstrumentConfiguration | undefined
  >();

  const configurationsQuery = useGetAllInstrumentConfigurations();
  const configurations = {
    ...configurationsQuery,
    data: configurationsQuery.data?.instrumentConfigurations ?? [],
  } as UseQueryResult<InstrumentConfiguration[], string>;
  const instrumentSecurityControlService = useInstrumentSecurityControlService();
  const complianceService = useComplianceService();
  const registrarService = useRegistrarService();
  const instrumentConfigurationFactory = useRegistryInstrumentConfigurationFactory();
  const pauseInstrumentConfiguration = usePauseInstrumentConfiguration();
  const unpauseInstrumentConfiguration = useUnpauseInstrumentConfiguration();
  const addComplianceLockInstrumentConfiguration = useAddComplianceLockInstrumentConfiguration();
  const removeComplianceLockInstrumentConfiguration =
    useRemoveComplianceLockInstrumentConfiguration();
  const addSecurityLockInstrumentConfiguration = useAddSecurityLockInstrumentConfiguration();
  const removeSecurityLockInstrumentConfiguration = useRemoveSecurityLockInstrumentConfiguration();
  const enableFractionalSupportInstrumentConfiguration =
    useEnableFractionalSupportInstrumentConfiguration();
  const disableFractionalSupportInstrumentConfiguration =
    useDisableFractionalSupportInstrumentConfiguration();
  const disableTransfersInstrumentConfiguration = useDisableTransfersInstrumentConfiguration();
  const enableTransfersInstrumentConfiguration = useEnableTransfersInstrumentConfiguration();
  const archiveInstrumentConfiguration = useArchiveInstrumentConfiguration();

  const mutate = useMutate();
  const { showBoundary } = useErrorBoundary<Error>();
  const { party } = useParties();

  const rows = configurations.data ?? [];

  const pause = useCallback(
    (row: Row) => {
      if (!instrumentSecurityControlService.data) {
        showBoundary(
          new Error(ERR_PAUSE_INST_CONF, {
            cause: 'Instrument security control service is not available.',
          }),
        );
        return Promise.resolve();
      }
      const payload = {
        instrumentSecurityControlServiceCid: instrumentSecurityControlService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };
      return mutate(
        pauseInstrumentConfiguration,
        payload,
        SUC_PAUSE_INST_CONF,
        ERR_PAUSE_INST_CONF,
      );
    },
    [instrumentSecurityControlService, pauseInstrumentConfiguration, mutate, showBoundary],
  );

  const unpause = useCallback(
    (row: Row) => {
      if (!instrumentSecurityControlService.data) {
        showBoundary(
          new Error(ERR_UNPAUSE_INST_CONF, {
            cause: 'Instrument security control service is not available.',
          }),
        );
        return Promise.resolve();
      }
      const payload = {
        instrumentSecurityControlServiceCid: instrumentSecurityControlService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };
      return mutate(
        unpauseInstrumentConfiguration,
        payload,
        SUC_UNPAUSE_INST_CONF,
        ERR_UNPAUSE_INST_CONF,
      );
    },
    [instrumentSecurityControlService, unpauseInstrumentConfiguration, mutate, showBoundary],
  );

  const disableFractionalSupport = useCallback(
    (row: Row) => {
      if (!registrarService.data) {
        showBoundary(
          new Error(ERR_DISABLE_FRACTIONAL_SUPPORT, {
            cause: 'Registrar service is not available.',
          }),
        );
        return Promise.resolve();
      }
      const payload = {
        registrarServiceCid: registrarService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };
      return mutate(
        disableFractionalSupportInstrumentConfiguration,
        payload,
        SUC_DISABLE_FRACTIONAL_SUPPORT,
        ERR_DISABLE_FRACTIONAL_SUPPORT,
      );
    },
    [registrarService, disableFractionalSupportInstrumentConfiguration, mutate, showBoundary],
  );

  const addComplianceLock = useCallback(
    (row: Row) => {
      if (!complianceService.data) {
        showBoundary(
          new Error('Failed to add compliance lock', {
            cause: 'Compliance service is not available.',
          }),
        );
        return Promise.resolve();
      }

      const payload = {
        complianceServiceCid: complianceService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };

      return mutate(
        addComplianceLockInstrumentConfiguration,
        payload,
        'Successfully added compliance lock',
        'Failed to add compliance lock',
      );
    },
    [complianceService, addComplianceLockInstrumentConfiguration, mutate, showBoundary],
  );

  const removeComplianceLock = useCallback(
    (row: Row) => {
      if (!complianceService.data) {
        showBoundary(
          new Error('Failed to remove compliance lock', {
            cause: 'Compliance service is not available.',
          }),
        );
        return Promise.resolve();
      }

      const payload = {
        complianceServiceCid: complianceService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };

      return mutate(
        removeComplianceLockInstrumentConfiguration,
        payload,
        'Successfully removed compliance lock',
        'Failed to remove compliance lock',
      );
    },
    [complianceService, removeComplianceLockInstrumentConfiguration, mutate, showBoundary],
  );

  const addSecurityLock = useCallback(
    (row: Row) => {
      if (!instrumentSecurityControlService.data) {
        showBoundary(
          new Error('Failed to add security lock', {
            cause: 'Instrument security control service is not available.',
          }),
        );
        return Promise.resolve();
      }

      const payload = {
        instrumentSecurityControlServiceCid: instrumentSecurityControlService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };

      return mutate(
        addSecurityLockInstrumentConfiguration,
        payload,
        'Successfully added security lock',
        'Failed to add security lock',
      );
    },
    [
      instrumentSecurityControlService,
      addSecurityLockInstrumentConfiguration,
      mutate,
      showBoundary,
    ],
  );

  const removeSecurityLock = useCallback(
    (row: Row) => {
      if (!instrumentSecurityControlService.data) {
        showBoundary(
          new Error('Failed to remove security lock', {
            cause: 'Instrument security control service is not available.',
          }),
        );
        return Promise.resolve();
      }

      const payload = {
        instrumentSecurityControlServiceCid: instrumentSecurityControlService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };

      return mutate(
        removeSecurityLockInstrumentConfiguration,
        payload,
        'Successfully removed security lock',
        'Failed to remove security lock',
      );
    },
    [
      instrumentSecurityControlService,
      removeSecurityLockInstrumentConfiguration,
      mutate,
      showBoundary,
    ],
  );

  const enableFractionalSupport = useCallback(
    (row: Row) => {
      if (!registrarService.data) {
        showBoundary(
          new Error(ERR_ENABLE_FRACTIONAL_SUPPORT, {
            cause: 'Registrar service is not available.',
          }),
        );
        return Promise.resolve();
      }
      const payload = {
        registrarServiceCid: registrarService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };
      return mutate(
        enableFractionalSupportInstrumentConfiguration,
        payload,
        SUC_ENABLE_FRACTIONAL_SUPPORT,
        ERR_ENABLE_FRACTIONAL_SUPPORT,
      );
    },
    [registrarService, enableFractionalSupportInstrumentConfiguration, mutate, showBoundary],
  );

  const disableTransfers = useCallback(
    (row: Row, disablementType: TransferDisablementType) => {
      if (!registrarService.data) {
        showBoundary(
          new Error(ERR_DISABLE_TRANSFERS_INST_CONF, {
            cause: 'Registrar service is not available.',
          }),
        );
        return Promise.resolve();
      }
      const payload = {
        registrarServiceCid: registrarService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
        disablementType,
      };
      return mutate(
        disableTransfersInstrumentConfiguration,
        payload,
        SUC_DISABLE_TRANSFERS_INST_CONF,
        ERR_DISABLE_TRANSFERS_INST_CONF,
      );
    },
    [registrarService, disableTransfersInstrumentConfiguration, mutate, showBoundary],
  );

  const enableTransfers = useCallback(
    (row: Row, disablementType: TransferDisablementType) => {
      if (!registrarService.data) {
        showBoundary(
          new Error(ERR_ENABLE_TRANSFERS_INST_CONF, {
            cause: 'Registrar service is not available.',
          }),
        );
        return Promise.resolve();
      }
      const payload = {
        registrarServiceCid: registrarService.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
        disablementType,
      };
      return mutate(
        enableTransfersInstrumentConfiguration,
        payload,
        SUC_ENABLE_TRANSFERS_INST_CONF,
        ERR_ENABLE_TRANSFERS_INST_CONF,
      );
    },
    [registrarService, enableTransfersInstrumentConfiguration, mutate, showBoundary],
  );

  const disableTransfersAction = useCallback(
    (row: Row) => disableTransfers(row, 'NoneOrNotProvided'),
    [disableTransfers],
  );

  const enableTransfersAction = useCallback(
    (row: Row) => enableTransfers(row, 'NoneOrNotProvided'),
    [enableTransfers],
  );

  const addGlobalLock = useCallback(
    (row: Row) => disableTransfers(row, 'GlobalLock'),
    [disableTransfers],
  );

  const removeGlobalLock = useCallback(
    (row: Row) => enableTransfers(row, 'GlobalLock'),
    [enableTransfers],
  );

  const addDOChill = useCallback(
    (row: Row) => disableTransfers(row, 'DOChill'),
    [disableTransfers],
  );

  const removeDOChill = useCallback(
    (row: Row) => enableTransfers(row, 'DOChill'),
    [enableTransfers],
  );

  const archive = useCallback(
    (row: Row) => {
      if (!registrarService.data) {
        showBoundary(
          new Error(ERR_ARC_INST_CONF, { cause: 'Registrar service is not available.' }),
        );
        return Promise.resolve();
      }
      if (!instrumentConfigurationFactory.data) {
        showBoundary(
          new Error(ERR_ARC_INST_CONF, {
            cause: 'InstrumentConfigurationFactory is not available.',
          }),
        );
        return Promise.resolve();
      }
      const payload = {
        registrarServiceCid: registrarService.data.contractId,
        instrumentConfigurationFactoryCid: instrumentConfigurationFactory.data.contractId,
        instrumentConfigurationCid: row.contract
          .contractId as ContractId<DamlInstrumentConfiguration>,
      };
      return mutate(archiveInstrumentConfiguration, payload, SUC_ARC_INST_CONF, ERR_ARC_INST_CONF);
    },
    [
      registrarService,
      instrumentConfigurationFactory,
      archiveInstrumentConfiguration,
      mutate,
      showBoundary,
    ],
  );

  const createActions = useCallback(
    (r: Row) => {
      const actions: Action[] = [];
      if (instrumentSecurityControlService.data?.payload.instrumentSecurityControl === party) {
        if (r.payload.pauseState === 'Unpaused') {
          actions.push({
            name: 'Pause',
            action: pause,
          });
        } else if (r.payload.pauseState === 'Paused') {
          actions.push({
            name: 'Unpause',
            action: unpause,
          });
        }
      }

      const hasDisablement = (disablementType: TransferDisablementType) =>
        r.payload.transferDisablements.includes(disablementType);

      if (instrumentSecurityControlService.data?.payload.instrumentSecurityControl === party) {
        if (hasDisablement('SecurityLock')) {
          actions.push({
            name: 'Remove Security Lock',
            action: removeSecurityLock,
          });
        } else {
          actions.push({
            name: 'Add Security Lock',
            action: addSecurityLock,
          });
        }
      }

      if (r.payload.compliance === party) {
        if (hasDisablement('ComplianceLock')) {
          actions.push({
            name: 'Remove Compliance Lock',
            action: removeComplianceLock,
          });
        } else {
          actions.push({
            name: 'Add Compliance Lock',
            action: addComplianceLock,
          });
        }
      }

      if (r.payload.registrar === party) {
        if (r.payload.fractionalSupport === 'Enabled') {
          actions.push({
            name: 'Disable Fractional',
            action: disableFractionalSupport,
          });
        } else if (r.payload.fractionalSupport === 'Disabled') {
          actions.push({
            name: 'Enable Fractional',
            action: enableFractionalSupport,
          });
        }
      }

      if (r.payload.registrar === party) {
        if (hasDisablement('NoneOrNotProvided')) {
          actions.push({
            name: 'Enable Transfers',
            action: enableTransfersAction,
          });
        } else {
          actions.push({
            name: 'Disable Transfers',
            action: disableTransfersAction,
          });
        }

        if (hasDisablement('GlobalLock')) {
          actions.push({
            name: 'Remove Global Lock',
            action: removeGlobalLock,
          });
        } else {
          actions.push({
            name: 'Add Global Lock',
            action: addGlobalLock,
          });
        }

        if (hasDisablement('DOChill')) {
          actions.push({
            name: 'Remove DO Chill',
            action: removeDOChill,
          });
        } else {
          actions.push({
            name: 'Add DO Chill',
            action: addDOChill,
          });
        }
      }

      if (r.payload.registrar === party) {
        actions.push(
          { name: 'Modify', action: () => Promise.resolve(setModifyInstrumentConfiguration(r)) },
          { name: 'Archive', action: archive },
        );
      }
      return actions;
    },
    [
      party,
      instrumentSecurityControlService.data,
      pause,
      unpause,
      enableFractionalSupport,
      disableFractionalSupport,
      addComplianceLock,
      removeComplianceLock,
      addSecurityLock,
      removeSecurityLock,
      enableTransfersAction,
      disableTransfersAction,
      removeGlobalLock,
      addGlobalLock,
      removeDOChill,
      addDOChill,
      archive,
    ],
  );

  const columns = useMemo(() => createHeaders(createActions), [createActions]);

  const { handleExport, isExportDisabled } = useCsvExport(columns, rows, {
    filename: 'instrument_configurations',
  });

  const isLoading =
    configurations.isLoading ||
    registrarService.isLoading ||
    instrumentSecurityControlService.isLoading ||
    archiveInstrumentConfiguration.isLoading;

  if (isLoading) return <Loading />;

  return (
    <>
      {createDtccTokenInstrumentConfigurationIsOpen && (
        <CreateDtccTokenInstrumentConfigurationDialog
          open={createDtccTokenInstrumentConfigurationIsOpen}
          onClose={() => setCreateDtccTokenInstrumentConfigurationIsOpen(false)}
        />
      )}
      {!!modifyInstrumentConfiguration && (
        <ModifyDtccTokenInstrumentConfigurationDialog
          open={!!modifyInstrumentConfiguration}
          onClose={() => setModifyInstrumentConfiguration(undefined)}
          instrumentConfiguration={modifyInstrumentConfiguration}
        />
      )}
      <QueryTable
        enableTableWrapper
        title="Configured Tokens"
        variant="h3"
        columns={columns}
        rowQuery={configurations}
        rowKey={(a) => a.contract.contractId}
        toolbar={() => (
          <Toolbar
            handleExport={handleExport}
            isExportDisabled={isExportDisabled}
            isCreateDisabled={!registrarService.data}
            isLoading={registrarService.isLoading}
            onCreateClick={() => setCreateDtccTokenInstrumentConfigurationIsOpen(true)}
          />
        )}
      />
    </>
  );
};
